/**
 * Gemini AI OCR Service
 * Uses Google Gemini API for intelligent certificate data extraction
 */

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent';

export class GeminiOCRService {
  
  constructor() {
    if (!GEMINI_API_KEY) {
      console.warn('⚠️ Gemini API key not configured. Set REACT_APP_GEMINI_API_KEY');
    }
  }

  /**
   * Convert image file to base64
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Create structured prompt for certificate data extraction
   */
  createExtractionPrompt() {
    return `
You are an expert OCR system specialized in extracting data from educational certificates. 

Analyze this certificate image and extract the following information in JSON format:

{
  "institution": "Institution/University name",
  "institution_id": "Institution code/ID (if visible)",
  "student_name": "Full student name",
  "student_id": "Student ID number (if visible)",
  "roll_number": "Roll number/Registration number",
  "certificate_type": "Type of certificate (e.g., 'Degree Certificate', 'Diploma', 'Mark Sheet', 'Transcript')",
  "program": "Course/Program name (e.g., 'Bachelor of Technology', 'Master of Science')",
  "batch": "Academic year/batch (e.g., '2020-2024', '2022')",
  "marks": "CGPA/Percentage/Grade (if visible)",
  "issue_date": "Certificate issue date",
  "verification_code": "Any verification/serial number",
  "additional_details": {
    "specialization": "Branch/Specialization",
    "honors": "Any honors/distinctions",
    "signatures": "Signature authorities mentioned",
    "seal_present": "Whether official seal is present (true/false)"
  }
}

IMPORTANT INSTRUCTIONS:
1. Extract ONLY the information that is clearly visible and readable in the image
2. Use "Not Found" for any field that cannot be determined
3. For institution_id, look for codes like university registration numbers, AICTE codes, etc.
4. Be precise with names and numbers - avoid assumptions
5. For certificate_type, be specific (e.g., "Provisional Certificate", "Final Degree Certificate")
6. For dates, use format: "DD/MM/YYYY" or "Month YYYY" as visible
7. Return valid JSON only, no additional text

If the image is not a certificate or is unreadable, return:
{
  "error": "Unable to process certificate image",
  "reason": "Specific reason for failure"
}
`;
  }

  /**
   * Extract certificate data using Gemini Vision API
   */
  async extractCertificateData(imageFile) {
    if (!GEMINI_API_KEY) {
      throw new Error('Gemini API key not configured');
    }

    try {
      console.log('🔍 Extracting certificate data with Gemini AI...');

      // Convert image to base64
      const base64Image = await this.fileToBase64(imageFile);
      
      // Determine image MIME type
      const mimeType = imageFile.type || 'image/jpeg';

      const requestBody = {
        contents: [
          {
            parts: [
              {
                text: this.createExtractionPrompt()
              },
              {
                inline_data: {
                  mime_type: mimeType,
                  data: base64Image
                }
              }
            ]
          }
        ],
        generationConfig: {
          temperature: 0.1,
          topK: 32,
          topP: 1,
          maxOutputTokens: 2048,
        }
      };

      const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Gemini API error: ${errorData.error?.message || response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.candidates || !data.candidates[0] || !data.candidates[0].content) {
        throw new Error('Invalid response from Gemini API');
      }

      const extractedText = data.candidates[0].content.parts[0].text;
      console.log('📄 Raw Gemini response:', extractedText);

      // Parse JSON response
      const jsonMatch = extractedText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON found in Gemini response');
      }

      const certificateData = JSON.parse(jsonMatch[0]);
      
      if (certificateData.error) {
        throw new Error(`Certificate processing failed: ${certificateData.reason}`);
      }

      // Post-process and validate data
      const processedData = this.postProcessCertificateData(certificateData);
      
      console.log('✅ Certificate data extracted successfully:', processedData);
      return processedData;

    } catch (error) {
      console.error('❌ Error extracting certificate data:', error);
      throw error;
    }
  }

  /**
   * Post-process and clean extracted data
   */
  postProcessCertificateData(rawData) {
    // Generate institution_id if not found
    if (!rawData.institution_id || rawData.institution_id === 'Not Found') {
      if (rawData.institution && rawData.institution !== 'Not Found') {
        rawData.institution_id = this.generateInstitutionId(rawData.institution);
      }
    }

    // Clean up fields
    const cleanedData = {
      institution: this.cleanField(rawData.institution),
      institution_id: this.cleanField(rawData.institution_id),
      student_name: this.cleanField(rawData.student_name),
      student_id: this.cleanField(rawData.student_id),
      roll_number: this.cleanField(rawData.roll_number),
      certificate_type: this.cleanField(rawData.certificate_type),
      program: this.cleanField(rawData.program),
      batch: this.cleanField(rawData.batch),
      marks: this.cleanField(rawData.marks),
      issue_date: this.cleanField(rawData.issue_date),
      verification_code: this.cleanField(rawData.verification_code),
      additional_details: rawData.additional_details || {},
      
      // Add extraction metadata
      extraction_metadata: {
        method: 'gemini_ai',
        timestamp: new Date().toISOString(),
        confidence: 'high',
        api_version: 'gemini-2.5-flash'
      }
    };

    return cleanedData;
  }

  /**
   * Clean individual field data
   */
  cleanField(value) {
    if (!value || value === 'Not Found' || value === 'null' || value === null) {
      return null;
    }
    return typeof value === 'string' ? value.trim() : value;
  }

  /**
   * Generate institution ID from institution name
   */
  generateInstitutionId(institutionName) {
    if (!institutionName) return null;
    
    return institutionName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .replace(/university|college|institute|school/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .substring(0, 20);
  }

  /**
   * Validate extracted certificate data
   */
  validateCertificateData(data) {
    const errors = [];
    
    if (!data.student_name) {
      errors.push('Student name is required');
    }
    
    if (!data.institution) {
      errors.push('Institution name is required');
    }
    
    if (!data.certificate_type) {
      errors.push('Certificate type is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Create fallback OCR data for non-Gemini scenarios
   */
  createFallbackOCR(fileName, basicText = '') {
    console.log('📝 Creating fallback OCR data');
    
    return {
      institution: null,
      institution_id: null,
      student_name: null,
      student_id: null,
      roll_number: null,
      certificate_type: 'Unknown Certificate',
      program: null,
      batch: null,
      marks: null,
      issue_date: null,
      verification_code: null,
      additional_details: {
        raw_text: basicText,
        fallback_reason: 'Gemini API unavailable'
      },
      extraction_metadata: {
        method: 'fallback',
        timestamp: new Date().toISOString(),
        confidence: 'low'
      }
    };
  }

  /**
   * Test Gemini API connection
   */
  async testConnection() {
    if (!GEMINI_API_KEY) {
      return { success: false, error: 'API key not configured' };
    }

    try {
      const testRequest = {
        contents: [
          {
            parts: [
              {
                text: "Respond with: 'Gemini API connection successful'"
              }
            ]
          }
        ]
      };

      const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testRequest)
      });

      if (response.ok) {
        console.log('✅ Gemini API connection successful');
        return { success: true };
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Gemini API connection failed:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
export const geminiOCR = new GeminiOCRService();

export default GeminiOCRService;